/**
 * Executive tornado answer view: analysis title, provenance caption, and
 * the tornado chart, over a kickoff + poll run.
 *
 * Unlike Query Forecast's synchronous answer, a tornado run is an async
 * worker calculation: this view POSTs /v2/executive/tornado/run (which
 * mints an AgentPlan run and returns its plan_id) and then polls
 * GET /v2/executive/tornado/{plan_id} until the run completes or fails,
 * showing calm phase labels from the run's progress along the way.
 *
 * The scenario pair is the parent's COMMITTED pair — changing the pane
 * dropdowns alone never re-runs; the parent copies draft → committed only
 * on the Update button, and the run effect here keys on the committed
 * pair (plus prompt id). runSeq guards stale responses exactly like
 * AnswerView.
 *
 * Provenance names the pair actually run (from the run result, not the
 * dropdowns) so screenshots carry their own attribution (design doc §8).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { CalcProgressRow } from 'src/components/AgenticV2/types';
import TornadoChartCore from 'src/components/Reports/PortfolioReportDetails/ReportEdit/Tornado/TornadoChartCore';
import { stripPromptMarkdown } from 'src/components/shared/PromptTitle';
import { useData } from 'src/hooks/useData';
import { RowIdentifier } from 'src/types/models';
import { HTTPError } from 'src/support/errors';

import { getTornadoRun, runTornado } from './api';
import { formatUtcTimestamp } from './formatUtcTimestamp';
import { EXEC_CONTENT_CONTAINER_SX } from './layout';
import ProcessingPanel from './ProcessingPanel';
import {
  ExecutiveBrand,
  ExecutiveTornadoPromptItem,
  TornadoPair,
  TornadoRunResult,
} from './types';

const POLL_INTERVAL_MS = 2500;
// Client-side cap, independent of the backend executor's own 900s cap —
// a run that outlives both is reported calmly, never spun on forever.
const POLL_MAX_MS = 15 * 60 * 1000;

// Duplo-scaled Highcharts font sizes (the Reports page keeps the chart
// core's smaller defaults; Highcharts px styles don't inherit MUI).
const EXEC_CHART_SIZES = {
  title: 20,
  barLabel: 15,
  axisTitle: 15,
  axisValue: 14,
  dataLabel: 13,
};

interface TornadoAnswerViewProps {
  brand: ExecutiveBrand;
  prompt: ExecutiveTornadoPromptItem;
  pair: TornadoPair;
  onBack: () => void;
}

interface TornadoAnswerState {
  status: 'loading' | 'done' | 'error';
  phase?: string;
  // Per-group calculation rows snapshotted by the executor while the worker runs (progress.calc, 2026-09-07).
  calc?: CalcProgressRow[];
  result?: TornadoRunResult;
  errorMessage?: string;
}

const describeError = (e: unknown): string => {
  if (e instanceof HTTPError) {
    if (e.status === 403 || e.status === 404) {
      return 'This analysis is not available for the current selection.';
    }
    if (e.status === 400) {
      return 'This analysis is not available for the selected scenarios.';
    }
    return 'The forecast service could not run this analysis right now.';
  }
  return 'Something went wrong while running this analysis.';
};

const phaseLabel = (phase?: string): string => {
  if (phase === 'charting') return 'Preparing your chart…';
  return 'Calculating scenario effects…';
};

const TornadoAnswerView: React.FC<TornadoAnswerViewProps> = ({
  brand,
  prompt,
  pair,
  onBack,
}) => {
  const [state, setState] = useState<TornadoAnswerState>({
    status: 'loading',
  });
  // Monotonic run counter so a superseded run's polls never win.
  const runSeq = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    runSeq.current += 1;
    const seq = runSeq.current;
    setState({ status: 'loading' });

    const startedAt = Date.now();

    const poll = (planId: string) => {
      timerRef.current = setTimeout(async () => {
        try {
          const run = await getTornadoRun(planId);
          if (runSeq.current !== seq) return;
          if (run.status === 'completed' && run.result) {
            setState({ status: 'done', result: run.result });
            return;
          }
          if (run.status === 'failed') {
            setState({
              status: 'error',
              errorMessage:
                run.progress?.error ??
                'The forecast service could not run this analysis right now.',
            });
            return;
          }
          if (Date.now() - startedAt > POLL_MAX_MS) {
            setState({
              status: 'error',
              errorMessage:
                'This analysis is taking too long. Please try again later.',
            });
            return;
          }
          setState({
            status: 'loading',
            phase: run.progress?.phase,
            calc: run.progress?.calc ?? undefined,
          });
          poll(planId);
        } catch (e: unknown) {
          if (runSeq.current !== seq) return;
          setState({ status: 'error', errorMessage: describeError(e) });
        }
      }, POLL_INTERVAL_MS);
    };

    runTornado(prompt.id, pair.aId, pair.bId)
      .then((resp) => {
        if (runSeq.current !== seq) return;
        poll(resp.plan_id);
      })
      .catch((e: unknown) => {
        if (runSeq.current !== seq) return;
        setState({ status: 'error', errorMessage: describeError(e) });
      });

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [prompt.id, pair.aId, pair.bId]);

  // FormatKey for the frozen output, resolved with the executive's own
  // token — the same client-side idiom ChartVizResult uses.
  const { data } = useData<{ rowIdentifiers?: RowIdentifier[] }>(
    () => ({
      rowIdentifiers: `/instances/${brand.model_instance_id}/row_identifiers`,
    }),
    [brand.model_instance_id]
  );

  const outputName = state.result?.output_name ?? null;
  const formatKey = useMemo(() => {
    if (!data?.rowIdentifiers || !outputName) return null;
    const match = data.rowIdentifiers.find(
      (r) => r.Type === 'Output' && r.Name === outputName
    );
    return match?.FormatKey ?? null;
  }, [data?.rowIdentifiers, outputName]);

  const chartRows = useMemo(
    () =>
      (state.result?.chart_rows ?? []).map((row) => ({
        ...row,
        Value: row.ColumnValue,
      })),
    [state.result?.chart_rows]
  );

  const provenance = useMemo(() => {
    if (state.status !== 'done' || !state.result) {
      return brand.model_instance_name;
    }
    const pairPart = [
      state.result.scenario_a_name,
      state.result.scenario_b_name,
    ]
      .filter(Boolean)
      .join(' vs ');
    // Analysis period (A2, corrected 2026-09-07): the frozen report's date range, resolved to labels by Agentic-API
    // and carried on the result as analysis_period — "2028" for a single-period report, "2026–2029" for a range.
    // The first cut read result.columns[].name here; those are the DRIVER columns (kept for the bar-label popover),
    // so the caption listed every driver and never showed a period. Null for runs older than 2026-09-08.
    const periodPart = state.result.analysis_period
      ? ` · ${state.result.analysis_period}`
      : '';
    const calculatedPart = state.result.calculated_at
      ? ` · Calculated ${formatUtcTimestamp(state.result.calculated_at)}`
      : '';
    return `${pairPart} — ${brand.model_instance_name}${periodPart}${calculatedPart}`;
  }, [state.status, state.result, brand.model_instance_name]);

  return (
    <Box sx={{ ...EXEC_CONTENT_CONTAINER_SX, py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
        Back to analyses
      </Button>

      {/* Headline is already bold, so the title's **bold** markup is stripped rather than rendered. */}
      <Typography variant="h4" sx={{ mb: 1 }}>
        {stripPromptMarkdown(prompt.title)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {provenance}
      </Typography>

      {state.status === 'loading' && (
        // Per-group rows appear once the executor's first calc snapshot lands (groups run in parallel on the
        // worker, so several show as running at once); spinner + phase label until then.
        <ProcessingPanel
          title={phaseLabel(state.phase)}
          rows={state.calc ?? []}
        />
      )}

      {state.status === 'error' && (
        <Alert severity="info" sx={{ maxWidth: 560 }}>
          {state.errorMessage}
        </Alert>
      )}

      {state.status === 'done' && state.result && (
        // Same outlined-card idiom as the Reports chart card (theme defaults
        // MuiCard to outlined), so the chart reads as a bounded artifact
        // instead of floating in the content column. Provenance stays
        // outside the frame for now (2026-08-31 review).
        <Card sx={{ background: 'white' }}>
          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <TornadoChartCore
              data={chartRows}
              formatKey={formatKey}
              outputName={outputName ?? ''}
              columns={state.result.columns ?? undefined}
              sizes={EXEC_CHART_SIZES}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TornadoAnswerView;
